/* hom: hom-nav.js (House of Maryam layer) — navigation behaviour.
   Loaded with defer after pubsub.js, global.js and hom-base.js (snippets/hom-head.liquid).
   Everything here only enhances server-rendered markup; without JS the menus still work as native
   <details> accordions and links.

   - HOM.tooltip: one tooltip module (WCAG 1.4.13). Markup contract:
       <li|span class="hom-tip [hom-tip--start|hom-tip--end]" data-hom-tip>
         <a|button …>…accessible name…</a|button>
         <span class="hom-tip__bubble" aria-hidden="true">Label</span>
       </li|span>
     or from script: HOM.tooltip.wrap(trigger, 'Label', { align: 'start' | 'end' }).
     CSS (assets/hom-nav.css) shows the bubble on hover and keyboard focus and keeps it while the
     pointer is on it; this script hides it on Esc until the pointer or focus leaves.
   - <hom-menu>: the shared menu tree (snippets/hom-menu-tree.liquid) — one-open-at-a-time fallback for
     browsers without <details name>, aria-expanded kept true, Esc handling, current link scrolled into view.
   - Phone menu (Zenith's <header-drawer>): menu tree put in place from its <template> once the page is
     idle, or on first use (while the sidebar is on), current branch re-opened on every open, bottom bar
     "Shop" button opens it.
   - <hom-announce>: the announcement bar cross-fade (pauses on hover and focus, Pause button,
     first message only with reduced motion).
   The desktop sidebar's position needs no script: assets/hom-nav.css places it with sticky positioning. */
(() => {
  const HOM = (window.HOM = window.HOM || {});
  if (HOM.navReady) return;
  HOM.navReady = true;

  const isEscape = (event) => event.key === 'Escape' || event.key === 'Esc' || event.code === 'Escape';
  const reducedMotion = () =>
    typeof HOM.reducedMotion === 'function'
      ? HOM.reducedMotion()
      : window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  /* ---------------------------------------------------------------- */
  /* Tooltips                                                         */
  /* ---------------------------------------------------------------- */
  HOM.tooltip = (() => {
    const SELECTOR = '[data-hom-tip]';
    const isShown = (tip) => tip.matches(':hover') || tip.matches(':focus-within');

    const hideAll = () => {
      let hidden = false;
      document.querySelectorAll(SELECTOR).forEach((tip) => {
        if (!tip.classList.contains('is-dismissed') && isShown(tip)) {
          tip.classList.add('is-dismissed');
          hidden = true;
        }
      });
      return hidden;
    };

    document.addEventListener('keydown', (event) => {
      if (isEscape(event)) hideAll();
    });

    // Reset once the pointer and focus have both left the tooltip.
    const reset = (event) => {
      const tip = event.target instanceof Element ? event.target.closest(SELECTOR) : null;
      if (!tip || !tip.classList.contains('is-dismissed')) return;
      const next = event.relatedTarget;
      if (next instanceof Node && tip.contains(next)) return;
      setTimeout(() => {
        if (!isShown(tip)) tip.classList.remove('is-dismissed');
      });
    };
    document.addEventListener('pointerout', reset);
    document.addEventListener('focusout', reset);

    /**
     * Give an existing trigger a tooltip from script (other areas can reuse this instead of new code).
     * The trigger must already have its own accessible name; the bubble only repeats it visually.
     * @param {HTMLElement} trigger - a link or button
     * @param {string} label - the tooltip text
     * @param {{align?: 'start'|'end'}} [options] - keep the bubble inside a container edge
     * @returns {HTMLElement|null} the .hom-tip wrapper
     */
    const wrap = (trigger, label, options = {}) => {
      if (!(trigger instanceof HTMLElement) || !label || !trigger.parentNode) return null;
      let tip = trigger.parentElement && trigger.parentElement.matches(SELECTOR) ? trigger.parentElement : null;
      if (!tip) {
        tip = document.createElement('span');
        tip.className = 'hom-tip';
        tip.setAttribute('data-hom-tip', '');
        trigger.parentNode.insertBefore(tip, trigger);
        tip.appendChild(trigger);
      }
      tip.classList.toggle('hom-tip--start', options.align === 'start');
      tip.classList.toggle('hom-tip--end', options.align === 'end');
      let bubble = tip.querySelector(':scope > .hom-tip__bubble');
      if (!bubble) {
        bubble = document.createElement('span');
        bubble.className = 'hom-tip__bubble';
        bubble.setAttribute('aria-hidden', 'true');
        tip.appendChild(bubble);
      }
      bubble.textContent = label;
      return tip;
    };

    return { SELECTOR, hideAll, wrap };
  })();

  /* ---------------------------------------------------------------- */
  /* Shared menu tree                                                 */
  /* ---------------------------------------------------------------- */
  const SUPPORTS_DETAILS_NAME = 'name' in HTMLDetailsElement.prototype;

  const summaryOf = (details) =>
    Array.from(details.children).find((child) => child.tagName === 'SUMMARY') || null;

  const syncExpanded = (details) => {
    const summary = summaryOf(details);
    // Zenith's header drawer adds role="button" + aria-expanded="false" to every summary inside it.
    if (summary && summary.hasAttribute('aria-expanded')) {
      summary.setAttribute('aria-expanded', String(details.open));
    }
  };

  class HomMenu extends HTMLElement {
    connectedCallback() {
      if (this.homReady) return;
      this.homReady = true;
      this.drawer = this.closest('header-drawer');

      this.onClickCapture = this.onClickCapture.bind(this);
      this.onKeyUp = this.onKeyUp.bind(this);
      this.onToggle = this.onToggle.bind(this);

      // Capture: runs before Zenith's own summary listeners inside the header drawer.
      this.addEventListener('click', this.onClickCapture, true);
      this.addEventListener('keyup', this.onKeyUp);
      this.addEventListener('toggle', this.onToggle, true);

      this.sync();
      if (this.dataset.context === 'sidebar') this.watchCurrent();
    }

    disconnectedCallback() {
      this.homReady = false;
      this.removeEventListener('click', this.onClickCapture, true);
      this.removeEventListener('keyup', this.onKeyUp);
      this.removeEventListener('toggle', this.onToggle, true);
      if (this.resizeObserver) this.resizeObserver.disconnect();
    }

    /**
     * Sidebar: scroll the menu so the current link is in view. The menu's height settles once Zenith
     * has measured the header (--header-height), so this re-runs on resize for a few seconds and stops
     * as soon as the visitor scrolls or uses the menu.
     */
    watchCurrent() {
      const scroller = this.closest('[data-hom-sidebar-scroll]');
      if (!scroller || !this.querySelector('[aria-current="page"]')) return;
      this.scroller = scroller;
      this.userMoved = false;
      const stop = () => {
        this.userMoved = true;
      };
      ['wheel', 'touchstart', 'pointerdown', 'keydown'].forEach((type) =>
        scroller.addEventListener(type, stop, { once: true, passive: true })
      );
      if ('ResizeObserver' in window) {
        this.resizeObserver = new ResizeObserver(() => this.revealCurrent());
        this.resizeObserver.observe(scroller);
        setTimeout(() => this.resizeObserver && this.resizeObserver.disconnect(), 4000);
      } else {
        requestAnimationFrame(() => this.revealCurrent());
      }
    }

    get branches() {
      return Array.from(this.querySelectorAll('details.hom-menu__branch'));
    }

    sync() {
      this.branches.forEach(syncExpanded);
    }

    /** Re-open the branch that holds the current page (Zenith closes every branch when its drawer closes). */
    openCurrent() {
      this.branches.forEach((details) => {
        if (details.hasAttribute('data-hom-current')) this.stamp(details);
        details.open = details.hasAttribute('data-hom-current');
        syncExpanded(details);
      });
    }

    /**
     * Speed: a closed sidebar branch sends its list as <template data-hom-lazy> (snippets/hom-menu-tree.liquid).
     * It goes into the page just before the branch opens: on the click (before the native toggle, so the list
     * shows in the same frame) or, for any other way of opening, on the toggle event.
     */
    stamp(details) {
      const lazy = Array.from(details.children).find((child) => child.tagName === 'TEMPLATE' && child.hasAttribute('data-hom-lazy'));
      if (lazy) lazy.replaceWith(lazy.content);
    }

    onClickCapture(event) {
      const summary = event.target instanceof Element ? event.target.closest('summary') : null;
      if (summary && this.contains(summary) && summary.parentElement instanceof HTMLDetailsElement) {
        this.stamp(summary.parentElement);
      }
      if (!this.drawer) return;
      // Zenith's HeaderDrawer treats nested summaries as slide-in submenus (focus trap, classes).
      // Our branches are plain accordions: let the native toggle happen, keep Zenith out.
      if (summary && this.contains(summary)) event.stopPropagation();
    }

    onKeyUp(event) {
      if (!isEscape(event)) return;
      if (this.drawer) {
        // In the phone menu Esc closes the whole menu (Zenith would only fold the open branch).
        event.stopPropagation();
        const openBtn = this.drawer.querySelector('summary.menu-drawer-icon');
        if (typeof this.drawer.closeMenuDrawer === 'function' && openBtn) this.drawer.closeMenuDrawer(openBtn);
        return;
      }
      // Sidebar: fold the branch around the focus and keep focus on its summary.
      const branch = event.target instanceof Element ? event.target.closest('details.hom-menu__branch[open]') : null;
      if (!branch || !this.contains(branch)) return;
      branch.open = false;
      const summary = summaryOf(branch);
      if (summary) summary.focus();
    }

    onToggle(event) {
      const details = event.target;
      if (!(details instanceof HTMLDetailsElement) || !details.classList.contains('hom-menu__branch')) return;
      if (details.open) this.stamp(details);
      syncExpanded(details);
      if (!details.open || SUPPORTS_DETAILS_NAME) return;
      const name = details.getAttribute('name');
      if (!name) return;
      this.branches.forEach((other) => {
        if (other !== details && other.open && other.getAttribute('name') === name) other.open = false;
      });
    }

    revealCurrent() {
      const current = this.querySelector('[aria-current="page"]');
      const scroller = this.scroller;
      if (this.userMoved || !current || !scroller || !current.offsetParent || !scroller.clientHeight) return;
      const box = scroller.getBoundingClientRect();
      // Before the page is scrolled, the bottom of the menu can sit under the sticky foot (shortcuts and
      // account line) or below the window (assets/hom-nav.css): only count the part that shows.
      let bottom = Math.min(box.bottom, window.innerHeight);
      const foot = scroller.parentElement ? scroller.parentElement.querySelector('.hom-sidebar__foot') : null;
      if (foot) bottom = Math.min(bottom, foot.getBoundingClientRect().top);
      const shown = Math.max(0, bottom - box.top);
      if (!shown) return;
      const item = current.getBoundingClientRect();
      const top = item.top - box.top + scroller.scrollTop;
      const visible = top >= scroller.scrollTop && top + item.height <= scroller.scrollTop + shown - 16;
      if (!visible) scroller.scrollTop = Math.max(0, top - shown / 3);
    }
  }

  if (!customElements.get('hom-menu')) customElements.define('hom-menu', HomMenu);

  /* ---------------------------------------------------------------- */
  /* Phone menu (Zenith header drawer)                                */
  /* ---------------------------------------------------------------- */
  const drawerSummary = () => document.querySelector('header-drawer summary.menu-drawer-icon');
  let openedFromBar = null;

  // While the sidebar is on, the phone menu's tree waits in <template data-hom-drawer-menu>
  // (snippets/hom-drawer.liquid): the sidebar holds the page's one real copy of the menu links, and the
  // page loads without the ~450 elements of this copy. It is put in place:
  //   - once the page has loaded and the browser is idle, when the menu button is on screen (phones,
  //     tablets). Adding the tree makes the browser restyle the whole page, so it is done before the
  //     first tap rather than inside it (measured at 4x CPU: about 200 ms off that first tap);
  //   - otherwise the first time a menu button is touched, focused or pressed, which is always before
  //     Zenith's drawer opens and builds its focus trap (desktop without the sidebar, a tap before
  //     the page has finished loading).
  // The new <hom-menu> sets itself up when connected. Its ids (HomMenu-drawer-…) differ from the
  // sidebar's (HomMenu-sidebar-…).
  const DRAWER_OPENERS = 'header-drawer summary.menu-drawer-icon, [data-hom-open-menu]';

  const stampDrawerMenu = () => {
    document.querySelectorAll('template[data-hom-drawer-menu]').forEach((template) => {
      const content = document.importNode(template.content, true);
      // The attributes Zenith's drawer gives its summaries when it starts (assets/global.js); this copy
      // arrives later, so screen readers hear the same "button, collapsed/expanded" as before.
      content.querySelectorAll('summary').forEach((summary) => {
        summary.setAttribute('role', 'button');
        summary.setAttribute('aria-expanded', String(summary.parentElement.open));
        const panel = summary.nextElementSibling;
        if (panel && panel.id) summary.setAttribute('aria-controls', panel.id);
      });
      template.replaceWith(content);
    });
  };

  const onDrawerOpenerEvent = (event) => {
    const target = event.target instanceof Element ? event.target : null;
    if (target && target.closest(DRAWER_OPENERS)) stampDrawerMenu();
  };
  // Capture phase: runs before Zenith's own click listener on the summary.
  ['pointerdown', 'focusin', 'click'].forEach((type) => document.addEventListener(type, onDrawerOpenerEvent, true));

  // No sidebar menu in the page (its section hidden or removed): the phone menu, which then also
  // serves desktop (assets/hom-nav.css), becomes the real copy at once.
  // A drawer opened natively before this script ran (no JavaScript yet) gets its menu now too.
  const ensureMenuCopy = () => {
    if (
      !document.querySelector('hom-menu[data-context="sidebar"]') ||
      document.querySelector('header-drawer details.menu-drawer-container[open]')
    ) {
      stampDrawerMenu();
    }
  };

  const menuButtonShown = () => {
    const summary = drawerSummary();
    return !!(summary && summary.getClientRects().length);
  };

  const prepareDrawerMenu = () => {
    if (!document.querySelector('template[data-hom-drawer-menu]') || !menuButtonShown()) return;
    const run = () => {
      if (menuButtonShown()) stampDrawerMenu();
    };
    if (typeof window.requestIdleCallback === 'function') window.requestIdleCallback(run, { timeout: 3000 });
    else setTimeout(run, 1500); // Safari has no requestIdleCallback
  };

  const whenLoaded = (callback) => {
    if (document.readyState === 'complete') callback();
    else window.addEventListener('load', callback, { once: true });
  };

  const syncBarButtons = (open) => {
    document.querySelectorAll('[data-hom-open-menu]').forEach((button) => {
      button.setAttribute('aria-expanded', String(open));
    });
  };

  const restoreBarFocus = () => {
    if (openedFromBar && openedFromBar.isConnected) openedFromBar.focus();
    openedFromBar = null;
  };

  // Zenith removes "menu-opening" as soon as the menu starts closing (Esc, close button, overlay) and
  // drops [open] 400ms later. React at the start, so the bottom bar button is updated and gets the
  // focus back at once.
  const watchDrawers = () => {
    document.querySelectorAll('header-drawer details.menu-drawer-container').forEach((details) => {
      if (details.homWatched) return;
      details.homWatched = true;
      new MutationObserver(() => {
        if (!details.open || details.classList.contains('menu-opening')) return;
        syncBarButtons(false);
        // After the whole click: Zenith's HeaderDrawer close handler runs after this observer and moves focus
        // to the header Menu button; returning it to the bar's Shop button must come last.
        setTimeout(restoreBarFocus, 0);
      }).observe(details, { attributes: true, attributeFilter: ['class'] });
    });
  };

  // The drawer's main <details> toggles on open and (after Zenith's 400ms close animation) on close.
  document.addEventListener(
    'toggle',
    (event) => {
      const details = event.target;
      if (!(details instanceof HTMLDetailsElement) || !details.classList.contains('menu-drawer-container')) return;
      if (!details.closest('header-drawer')) return;
      syncBarButtons(details.open);
      if (details.open) {
        // Keyboard users: the open drawer covers the Menu button, so focus moves to the drawer's Close
        // button, trapped inside the drawer panel (Esc and Close still return it to the Menu button).
        const summary = details.querySelector(':scope > summary');
        const panel = details.querySelector('#menu-drawer');
        const close = panel && panel.querySelector('#mobile-menu-close-btn');
        if (summary && close && document.activeElement === summary && typeof window.trapFocus === 'function') {
          // The drawer is still hidden (visibility) for a moment while Zenith starts its animation, and a
          // hidden button cannot take focus: wait for it, unless focus moved elsewhere meanwhile.
          const focusClose = (tries) => {
            if (!details.open || document.activeElement !== summary) return;
            if (getComputedStyle(close).visibility === 'visible') window.trapFocus(panel, close);
            else if (tries < 60) requestAnimationFrame(() => focusClose(tries + 1));
          };
          requestAnimationFrame(() => focusClose(0));
        }
        // Safety net: a drawer opened some other way than its buttons still gets its menu tree.
        stampDrawerMenu();
        details.querySelectorAll('hom-menu').forEach((menu) => {
          if (typeof menu.openCurrent === 'function') menu.openCurrent();
        });
      } else {
        restoreBarFocus();
      }
    },
    true
  );

  document.addEventListener('click', (event) => {
    const button = event.target instanceof Element ? event.target.closest('[data-hom-open-menu]') : null;
    if (!button) return;
    const summary = drawerSummary();
    if (!summary) return;
    event.preventDefault();
    const details = summary.parentElement;
    if (details && details.hasAttribute('open')) {
      const drawer = summary.closest('header-drawer');
      if (drawer && typeof drawer.closeMenuDrawer === 'function') drawer.closeMenuDrawer(summary);
      return;
    }
    openedFromBar = button;
    summary.click();
  });

  // Hide the bottom bar's Shop button when the page has no phone menu to open.
  const checkBarButtons = () => {
    const hasDrawer = !!drawerSummary();
    document.querySelectorAll('[data-hom-open-menu]').forEach((button) => {
      const item = button.closest('li') || button;
      item.hidden = !hasDrawer;
    });
  };

  /* ---------------------------------------------------------------- */
  /* Announcement bar                                                 */
  /* ---------------------------------------------------------------- */
  class HomAnnounce extends HTMLElement {
    connectedCallback() {
      this.items = Array.from(this.querySelectorAll('[data-hom-announce-item]'));
      this.list = this.querySelector('.hom-announce__list');
      this.prev = this.querySelector('[data-hom-announce-prev]');
      this.next = this.querySelector('[data-hom-announce-next]');
      this.interval = Math.max(3000, parseInt(this.dataset.interval, 10) || 5000);
      this.index = Math.max(
        0,
        this.items.findIndex((item) => item.classList.contains('is-active'))
      );
      // A stop (an arrow was used) survives a move in the DOM (theme editor reorder); hover and focus do not.
      if (typeof this.stopped !== 'boolean') this.stopped = false;
      this.hovered = false;
      this.focused = false;
      this.selected = false;
      if (this.items.length < 2) return;

      // Handlers are made once, so a reconnect (element moved) re-adds the same functions and a click
      // never runs twice; disconnectedCallback removes every one of them.
      if (!this.homHandlers) this.createHandlers();
      this.listen(true);
      this.update();
    }

    disconnectedCallback() {
      clearTimeout(this.timer);
      if (this.homHandlers) this.listen(false);
    }

    createHandlers() {
      this.homHandlers = true;
      this.motionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
      this.onPointerEnter = () => {
        this.hovered = true;
        this.schedule();
      };
      this.onPointerLeave = () => {
        this.hovered = false;
        this.schedule();
      };
      // Focus anywhere in the bar pauses (and makes the list a polite live region before an arrow is
      // pressed, so the first message chosen is read). Focus on a message link brings that message to the front.
      this.onFocusIn = (event) => {
        this.focused = true;
        // Keyboard focus (html.hom-kbd, assets/hom-base.js) stops the rotation for good, as in the WAI-ARIA
        // carousel pattern; a pointer only pauses it while it is over the bar.
        if (document.documentElement.classList.contains('hom-kbd')) this.stopped = true;
        const item = event.target instanceof Element ? event.target.closest('[data-hom-announce-item]') : null;
        if (item) this.show(this.items.indexOf(item));
        this.schedule();
      };
      this.onFocusOut = (event) => {
        if (event.relatedTarget instanceof Node && this.contains(event.relatedTarget)) return;
        this.focused = false;
        this.schedule();
      };
      // An arrow shows the previous or next message and stops the rotation for good (WCAG 2.2.2).
      this.onArrowClick = (event) => {
        const step = event.currentTarget === this.prev ? -1 : 1;
        this.stopped = true;
        this.classList.add('is-manual');
        this.show(this.index + step + this.items.length);
        this.schedule();
      };
      this.onMotionChange = () => this.update();
      this.onVisibility = () => this.schedule();
      this.onBlockSelect = (event) => {
        const item = event.target instanceof Element ? event.target.closest('[data-hom-announce-item]') : null;
        if (!item || !this.contains(item)) return;
        this.selected = event.type === 'shopify:block:select';
        if (this.selected) this.show(this.items.indexOf(item));
        this.schedule();
      };
    }

    /** @param {boolean} on - add (true) or remove (false) every listener */
    listen(on) {
      const method = on ? 'addEventListener' : 'removeEventListener';
      this[method]('pointerenter', this.onPointerEnter);
      this[method]('pointerleave', this.onPointerLeave);
      this[method]('focusin', this.onFocusIn);
      this[method]('focusout', this.onFocusOut);
      // The arrows may have been replaced (theme editor re-render): drop the old ones' listeners first.
      (this.listenedArrows || []).forEach((button) => button.removeEventListener('click', this.onArrowClick));
      this.listenedArrows = on ? [this.prev, this.next].filter(Boolean) : [];
      this.listenedArrows.forEach((button) => button.addEventListener('click', this.onArrowClick));
      if (this.motionQuery[method]) this.motionQuery[method]('change', this.onMotionChange);
      document[method]('visibilitychange', this.onVisibility);
      document[method]('shopify:block:select', this.onBlockSelect);
      document[method]('shopify:block:deselect', this.onBlockSelect);
    }

    get running() {
      return (
        !this.stopped &&
        !this.hovered &&
        !this.focused &&
        !this.selected &&
        !document.hidden &&
        !this.motionQuery.matches &&
        !document.documentElement.classList.contains('hom-motion-paused')
      );
    }

    update() {
      const reduce = this.motionQuery.matches || reducedMotion();
      if (reduce && !this.stopped && !this.focused && !this.selected) this.show(0);
      this.schedule();
    }

    show(index) {
      const count = this.items.length;
      if (!count || index < 0) return;
      this.index = index % count;
      this.items.forEach((item, i) => item.classList.toggle('is-active', i === this.index));
    }

    schedule() {
      clearTimeout(this.timer);
      const running = this.running;
      // Live region only while the bar stands still (WAI-ARIA carousel pattern): no chatter while rotating.
      if (this.list) this.list.setAttribute('aria-live', running ? 'off' : 'polite');
      if (!running) return;
      this.timer = setTimeout(() => {
        this.show(this.index + 1);
        this.schedule();
      }, this.interval);
    }
  }

  if (!customElements.get('hom-announce')) customElements.define('hom-announce', HomAnnounce);

  /* ---------------------------------------------------------------- */
  /* Sidebar end room                                                 */
  /* ---------------------------------------------------------------- */
  // The sidebar is as tall as the window under the sticky header, so while anything above it is still on
  // screen (announcement bar, a desktop search bar, a header that is not sticky) its bottom hangs below
  // the window and the sticky foot covers the end of the menu. Measure exactly how much, and give the
  // scroll area that much room at its end (--hom-sb-under; the CSS value is the first-paint estimate).
  let sidebarFrame = 0;
  const measureSidebar = () => {
    sidebarFrame = 0;
    const sidebar = document.querySelector('.hom-sidebar');
    if (!sidebar || !sidebar.offsetParent) return;
    const below = Math.max(0, Math.ceil(sidebar.getBoundingClientRect().bottom - document.documentElement.clientHeight));
    sidebar.style.setProperty('--hom-sb-under', below + 'px');
  };
  const queueSidebar = () => {
    if (!sidebarFrame) sidebarFrame = requestAnimationFrame(measureSidebar);
  };
  let sidebarWatched = false;
  const watchSidebar = () => {
    queueSidebar();
    if (sidebarWatched) return;
    sidebarWatched = true;
    window.addEventListener('scroll', queueSidebar, { passive: true });
    window.addEventListener('resize', queueSidebar, { passive: true });
    // The header sliding in or out (sticky "on scroll up") moves the sidebar after the scroll has ended.
    document.addEventListener('transitionend', queueSidebar, { passive: true });
    if ('ResizeObserver' in window) {
      const observer = new ResizeObserver(queueSidebar);
      document.querySelectorAll('.shopify-section-group-header-group').forEach((section) => observer.observe(section));
    }
  };

  /* ---------------------------------------------------------------- */
  /* Start + theme editor                                             */
  /* ---------------------------------------------------------------- */
  const init = () => {
    ensureMenuCopy();
    whenLoaded(prepareDrawerMenu);
    checkBarButtons();
    watchDrawers();
    watchSidebar();
  };
  init();

  document.addEventListener('shopify:section:load', init);
  document.addEventListener('shopify:section:unload', () => requestAnimationFrame(init));
})();
